import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Save, FileText, Megaphone } from 'lucide-react';
import toast from 'react-hot-toast';

type PageContent = {
  id: string;
  title: string;
  content: string;
  slug: string;
  is_active: boolean;
  created_at: string;
};

type Announcement = {
  id: string;
  title: string;
  content: string;
  type: 'ad' | 'message';
  is_active: boolean;
  created_at: string;
};

export default function PageContent() {
  const [pages, setPages] = useState<PageContent[]>([]);
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingPage, setEditingPage] = useState<Partial<PageContent> | null>(null);
  const [editingAnnouncement, setEditingAnnouncement] = useState<Partial<Announcement> | null>(null);

  useEffect(() => {
    loadPages();
    loadAnnouncements();
  }, []);

  const loadPages = async () => {
    try {
      const { data, error } = await supabase
        .from('page_content')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPages(data || []);
    } catch (error: any) {
      toast.error('Failed to load pages');
      console.error('Error:', error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadAnnouncements = async () => {
    try {
      const { data, error } = await supabase
        .from('announcements')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setAnnouncements(data || []);
    } catch (error: any) {
      toast.error('Failed to load announcements');
      console.error('Error:', error.message);
    }
  };

  const handleSavePage = async () => {
    if (!editingPage?.title || !editingPage?.content) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const { data, error } = await supabase
        .from('page_content')
        .upsert({
          ...editingPage,
          updated_at: new Date().toISOString()
        })
        .select()
        .single();

      if (error) throw error;

      toast.success(editingPage.id ? 'Page updated successfully' : 'Page created successfully');
      setEditingPage(null);
      await loadPages();
    } catch (error: any) {
      toast.error('Failed to save page');
      console.error('Error:', error.message);
    }
  };

  const handleSaveAnnouncement = async () => {
    if (!editingAnnouncement?.title || !editingAnnouncement?.content) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const { data, error } = await supabase
        .from('announcements')
        .upsert({
          ...editingAnnouncement,
          updated_at: new Date().toISOString()
        })
        .select()
        .single();

      if (error) throw error;

      toast.success(editingAnnouncement.id ? 'Announcement updated successfully' : 'Announcement created successfully');
      setEditingAnnouncement(null);
      await loadAnnouncements();
    } catch (error: any) {
      toast.error('Failed to save announcement');
      console.error('Error:', error.message);
    }
  };

  const handleDeleteAnnouncement = async (id: string) => {
    if (!confirm('Are you sure you want to delete this announcement?')) return;

    try {
      const { error } = await supabase
        .from('announcements')
        .delete()
        .eq('id', id);

      if (error) throw error;
      toast.success('Announcement deleted successfully');
      await loadAnnouncements();
    } catch (error: any) {
      toast.error('Failed to delete announcement');
      console.error('Error:', error.message);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-white flex items-center space-x-2">
          <FileText className="h-6 w-6" />
          <span>Content Management</span>
        </h2>
      </div>

      {/* Announcements Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-white flex items-center space-x-2">
            <Megaphone className="h-5 w-5" />
            <span>Announcements</span>
          </h3>
          <button
            onClick={() => setEditingAnnouncement({ type: 'message', is_active: true })}
            className="px-4 py-2 bg-cyan-500 hover:bg-cyan-600 text-white rounded-md transition-colors"
          >
            Add Announcement
          </button>
        </div>

        {editingAnnouncement && (
          <div className="bg-blue-900/40 backdrop-blur-sm rounded-lg p-6 border border-blue-500/20">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-200 mb-2">
                  Title
                </label>
                <input
                  type="text"
                  value={editingAnnouncement.title || ''}
                  onChange={(e) => setEditingAnnouncement({ ...editingAnnouncement, title: e.target.value })}
                  className="w-full px-3 py-2 bg-black/20 border border-gray-600 rounded-md text-white"
                  placeholder="Enter announcement title"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-200 mb-2">
                  Content
                </label>
                <textarea
                  value={editingAnnouncement.content || ''}
                  onChange={(e) => setEditingAnnouncement({ ...editingAnnouncement, content: e.target.value })}
                  className="w-full h-32 px-3 py-2 bg-black/20 border border-gray-600 rounded-md text-white"
                  placeholder="Enter announcement content"
                />
              </div>

              <div className="flex items-center space-x-4">
                <div>
                  <label className="block text-sm font-medium text-gray-200 mb-2">
                    Type
                  </label>
                  <select
                    value={editingAnnouncement.type}
                    onChange={(e) => setEditingAnnouncement({ ...editingAnnouncement, type: e.target.value as 'ad' | 'message' })}
                    className="px-3 py-2 bg-black/20 border border-gray-600 rounded-md text-white"
                  >
                    <option value="message">Message</option>
                    <option value="ad">Advertisement</option>
                  </select>
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={editingAnnouncement.is_active}
                    onChange={(e) => setEditingAnnouncement({ ...editingAnnouncement, is_active: e.target.checked })}
                    className="h-4 w-4 text-cyan-500"
                  />
                  <label className="text-sm text-gray-200">Active</label>
                </div>
              </div>

              <div className="flex justify-end space-x-4">
                <button
                  onClick={() => setEditingAnnouncement(null)}
                  className="px-4 py-2 text-white hover:text-gray-300 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveAnnouncement}
                  className="px-4 py-2 bg-cyan-500 hover:bg-cyan-600 text-white rounded-md transition-colors"
                >
                  Save Announcement
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 gap-4">
          {announcements.map((announcement) => (
            <div
              key={announcement.id}
              className={`bg-blue-900/40 backdrop-blur-sm rounded-lg p-4 border ${
                announcement.type === 'ad' ? 'border-purple-500/20' : 'border-cyan-500/20'
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <div>
                  <h4 className="text-lg font-medium text-white">{announcement.title}</h4>
                  <p className="text-sm text-gray-400">
                    {new Date(announcement.created_at).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    announcement.is_active
                      ? 'bg-green-500/20 text-green-400'
                      : 'bg-gray-500/20 text-gray-400'
                  }`}>
                    {announcement.is_active ? 'Active' : 'Inactive'}
                  </span>
                  <button
                    onClick={() => setEditingAnnouncement(announcement)}
                    className="text-cyan-400 hover:text-cyan-300"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDeleteAnnouncement(announcement.id)}
                    className="text-red-400 hover:text-red-300"
                  >
                    Delete
                  </button>
                </div>
              </div>
              <p className="text-gray-300">{announcement.content}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Page Content Section */}
      <div className="grid grid-cols-1 gap-6">
        {['privacy-policy', 'terms-of-service', 'contact'].map((slug) => {
          const page = pages.find(p => p.slug === slug) || {
            title: slug.split('-').map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(' '),
            content: '',
            slug,
            is_active: true
          };

          return (
            <div key={slug} className="bg-blue-900/40 backdrop-blur-sm rounded-lg p-6 border border-blue-500/20">
              <h3 className="text-lg font-medium text-white mb-4">{page.title}</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-200 mb-2">
                    Content
                  </label>
                  <textarea
                    value={editingPage?.slug === slug ? editingPage.content : page.content}
                    onChange={(e) => setEditingPage({ ...page, content: e.target.value })}
                    className="w-full h-64 px-3 py-2 bg-black/20 border border-gray-600 rounded-md text-white"
                    placeholder="Enter page content (Markdown supported)"
                  />
                </div>

                <div className="flex justify-end space-x-4">
                  {editingPage?.slug === slug ? (
                    <>
                      <button
                        onClick={() => setEditingPage(null)}
                        className="px-4 py-2 text-white hover:text-gray-300 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSavePage}
                        className="px-4 py-2 bg-cyan-500 hover:bg-cyan-600 text-white rounded-md transition-colors"
                      >
                        Save Changes
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => setEditingPage(page)}
                      className="px-4 py-2 text-cyan-400 hover:text-cyan-300 transition-colors"
                    >
                      Edit
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
